import {NgModule} from "@angular/core";
import {
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDatepickerI18n,
  NgbModule,
  NgbNavModule
} from "@ng-bootstrap/ng-bootstrap";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {SwiperModule} from "swiper/angular";
import {MatListModule} from "@angular/material/list";
import {MatBottomSheetModule} from "@angular/material/bottom-sheet";
import {MatIconModule} from "@angular/material/icon";
import {MatButtonModule} from "@angular/material/button";
import {InfiniteScrollModule} from "ngx-infinite-scroll";
import {NgxChartsModule} from "@swimlane/ngx-charts";
import {MatStepperModule} from "@angular/material/stepper";
import {MatDialogModule} from "@angular/material/dialog";
import {NgxMaskModule} from "ngx-mask";
import {DecimalPipe, NgOptimizedImage} from "@angular/common";
import {MatTooltipModule} from "@angular/material/tooltip";
import {RouteReuseStrategy, RouterModule} from "@angular/router";
import {ShareIconsModule} from "ngx-sharebuttons/icons";
import {ShareButtonsModule} from "ngx-sharebuttons/buttons";
import {
  GoogleSigninButtonModule,
  SocialLoginModule
} from "@abacritt/angularx-social-login";
import {LyIconModule, LyIconService} from "@alyle/ui/icon";
import {LyButtonModule} from "@alyle/ui/button";
import {LySliderModule} from "@alyle/ui/slider";
import {LyImageCropperModule} from "@alyle/ui/image-cropper";
import {
  AccountStatementsService,
  ActionService,
  AdministratorsService, ApplicationFormSettingsService,
  AuthenticationService, CategoriesService,
  CodebookService,
  CompetitorResultsService,
  CompetitorService,
  CustomNgbDatepickerAdapter,
  CustomNgbDatepickerDateParserFormatter,
  CustomNgbDatepickerI18n, DiscountsService,
  ErrorService,
  EventPaymentSettingsService,
  EventRaceService,
  EventService, ExportService,
  FormParametrizationService, ImageService, LeagueService,
  LiveResultsService,
  LoaderService,
  LocalStorageService, MessageService,
  OrganiserPaymentDataService,
  OrganiserService,
  OrganiserUsersService,
  PermissionService, RaceCommercialService, RaceRouteService,
  RaceService,
  RequestService, ToastService,
  UserDetailService, WaypointService, WsPayService
} from "@services";
import {
  CurrencyPipe,
  FormatTimePipe,
  LeagueEventsToGoPipe,
  LocalizedDatePipe,
  RaceLengthFormatPipe,
  RolePipe,
  LeagueRoundPipe,
  ErrorMessagesPipe,
  RaceCompetitorCountFormatPipe,
  RaceAscentDescentFormatPipe,
  RaceAltitudeFormatPipe,
  OrdinalNumberPipe,
  CompetitorFieldValuePipe,
  CategoryTypePipe,
  RaceCommercialItemsMinPriceFormatPipe,
  WaypointLocationPipe,
  TimeLimitPipe,
  ImportStatusPipe,
  RegistrationTypePipe,
  RegistrationNamePipe,
  LeagueNextRoundPipe,
  LiveResultsFinishedCountPipe,
  FilterByFieldPipe
} from "@pipes";
import {AppRouteReuseStrategy} from "./app-route-reuse.strategy";
import {LY_THEME, LY_THEME_NAME, LyRippleService, LyTheme2} from "@alyle/ui";
import {MinimaDark, MinimaLight} from "@alyle/ui/themes/minima";
import {ColorMonthPipe} from "./directive/month-color.directive";
import {CustomControlValueAccessorDirective} from "./directive/custom-control.directive";
import {ImageFallbackDirective} from "./directive/image-fallback.directive";
import {NumberFormatterDirective} from "./directive/format-number.directive";
import {
  ActionButtonComponent,
  ActionItemComponent,
  AddDiscountItemModalComponent,
  AdditionalPricingItemValueInputComponent,
  AddUserModalComponent,
  AdminCardWithTitleComponent,
  AdminLoadMoreComponent,
  AdminMenuComponent,
  AlertErrorComponent,
  AlertSuccessComponent,
  AlertWarningComponent,
  AppStoreInfoModalContent,
  BadgeComponent,
  TableHeaderSeparatorComponent,
  BadgeWithButtonComponent,
  BreadcrumbsComponent,
  ButtonComponent,
  ButtonWithIconComponent,
  CarouselComponent,
  CheckboxComponent,
  CheckmarkIndicatorComponent,
  ConfirmationModalComponent,
  RaceProfileModalComponent,
  RegistrationDetailsModalComponent,
  OrganiserContactModalComponent,
  DateRangeSelectorComponent,
  DateTimeFromToInputComponent,
  DateTimeInputComponent,
  DotIndicatorComponent,
  DownloadAppHeroComponent,
  DragAndDropComponent,
  DurationInputComponent,
  EmptyStateAdminComponent,
  EntryListItemComponent,
  DropdownComponent,
  DropdownContentDirective,
  DropdownItemDirective,
  DropdownToggleDirective,
  EventGridItemComponent,
  EventGridItemWithImage,
  LeagueGridItemWithImage,
  EventGridItemWithImageSmall,
  FeaturedRaceCarouselComponent,
  FieldConfigValueInputComponent,
  FooterComponent,
  FormCardComponent,
  GridItemTitleComponent,
  InputComponent,
  CheckpointInputComponent,
  LanguageInputComponent,
  LanguageInputSwitcherComponent,
  LanguageOutputComponent,
  LanguageSwitcherComponent,
  LiveEventComponent,
  LiveRaceListItemComponent,
  LiveResultsMobileItemComponent,
  LiveResultsRaceHeadingComponent,
  LiveResultsTableBodyComponent,
  LiveResultsTableHeaderComponent,
  LoaderComponent,
  LogoXxlComponent,
  MonthsPickerComponent,
  MultiSelectDropdownComponent,
  NavTabComponent,
  NavTabsComponent,
  PageHeaderPublicComponent,
  PageHeadingComponent,
  PanelComponent,
  RaceDetailsPanelComponent,
  RaceGridItemComponent,
  RacesHeaderComponent,
  RibbonComponent,
  SearchBarComponent,
  SearchBarEventsComponent,
  SearchBarRacesComponent,
  SearchComponent,
  SelectComponent,
  GreyCardWithTitleComponent,
  HorizontalBoxListComponent,
  HorizontalBoxListItemComponent,
  HeaderAltComponent,
  HeaderBadgeComponent,
  EventListItemComponent,
  FullCropperWidthComponent,
  ResultListItemComponent,
  MailboxListItemComponent,
  ActionButtonsRowComponent,
  AdminActionsHeaderComponent,
  ControlPointsTableComponent,
  RaceProfileComponent,
  EventListItemHeaderComponent,
  EventListItemBigComponent,
  EmptyStateComponent,
  SectionDividerComponent,
  PageHeaderComponent,
  TagsInputComponent,
  RacePricingTableComponent,
  TooltipComponent,
  TooltipDirective,
  ToastComponent,
  PageTemplateComponent,
  CompetitorTableComponent,
  ResultsTableComponent,
  ThreeDotsComponent,
  SearchRoundedComponent,
  ValidationModalComponent,
  ValidatorResultMessage,
  NewOrganiserModalComponent,
  ContactsTabComponent,
  SeriesTableComponent,
  TableSectionHeaderComponent,
  ShareFavouriteCtaComponent,
  TeamListItemComponent,
  InfoSectionPublicComponent,
  YoutubeEmbedComponent,
  SectionSubtitleComponent,
  TextareaComponent,
  TelInputComponent,
  StepperComponent,
  EventsListModalComponent,
  UserListModalComponent,
  SortableTableComponent,
  SpinnerComponent,
  SectionCardComponent, RoundedButtonComponent, RichTextEditorWrapperComponent
} from "@components";
import {
  AdminEventDetailsComponent,
  AdminRaceDetailsComponent,
  CategoriesListComponent,
  CategoryFormComponent,
  CategoryItemFormComponent,
  ChangePasswordOnRecoveryComponent,
  ContactComponent,
  DiscountsDetailsComponent,
  ErrorStateComponent,
  EventBasicDataComponent,
  EventCalendarComponent,
  LeagueCalendarComponent,
  EventDetailsComponent,
  EventInfoComponent,
  EventPaymentSettingsComponent,
  EventPreviewComponent,
  UserProfileDashboardBodyComponent,
  LandingComponent,
  ListOfAdministratorsComponent,
  ListOfDiscountsComponent,
  ListOfEventsComponent,
  ListOfOrganisersComponent,
  ListOfRequestsComponent,
  ListOfUsersComponent,
  LiveResultsComponent,
  LiveResultsRaceDetailsComponent,
  LoginComponent,
  MessagesComponent,
  OrganiserBasicDataComponent,
  OrganiserDetailsComponent,
  EventManagementComponent,
  OrganiserPaymentDataFormComponent,
  OrganiserUserListComponent,
  OrganizerDashboardComponent,
  PaymentComponent,
  PrivacyPolicyComponent,
  RaceAdditionalPricingItemCardComponent,
  RaceAdditionalPricingItemFormComponent,
  RaceCommercialItemCardComponent,
  RaceCommercialItemFormComponent,
  RaceDetailsAdditionalInfoComponent,
  RaceDetailsBasicInfoComponent,
  RaceDetailsComponent,
  RaceDetailsPricingComponent,
  RaceDetailsRegistrationSettingsComponent,
  RaceDetailsRegistrationSettingsCustomFieldFormComponent,
  RaceDetailsRegistrationSettingsDefaultFieldFormComponent,
  RaceDetailsSettingsComponent,
  RaceWaypointsComponent,
  RecoverLostPasswordComponent,
  RegisterToRaceComponent,
  RegisterToRaceItemComponent,
  RegisterToRaceFeeItemOutputComponent,
  RegisterToRaceAdditionalItemOutput,
  RequestDetailsComponent,
  SignupComponent,
  SignupVerificationComponent,
  UserDetailsComponent,
  UserDetailsDataComponent,
  UserProfileDashboardComponent,
  UserProfileEditComponent,
  UserProfileViewComponent,
  UserProfileDashboardTabComponent,
  UserProfileEventsTabComponent,
  UserProfileWishListTabComponent,
  UserProfileClubsAndTeamsTabComponent,
  RaceResultsListComponentComponent,
  RaceManagementComponentComponent,
  RaceCompetitorsListComponentComponent,
  EventInfoTabComponent,
  LeagueInfoTabComponent,
  LeagueResultsTabComponent,
  LeagueDetailsComponent,
  ListOfAccountStatementsComponent,
  AccountStatementDetailsComponent,
  ListOfLeaguesComponent,
  EventManagementRaceListComponent,
  EventManagementParticipantsListComponent,
  EventManagementRegistrationsListComponent,
  AdminLeagueDetailsComponent,
  LeagueBasicDataComponent,
  RaceMapTabComponent,
  LeagueEventListTabComponent, AdminTemplateComponent,
} from "@pages";
import {SortableColumn} from "./directive/sortable-column.directive";
import {TranslocoModule} from "@ngneat/transloco";
import {LeafletMapsWrapperComponent} from '@components';

@NgModule({
  declarations: [
    AdditionalPricingItemValueInputComponent,
    ActionButtonComponent,
    ActionItemComponent,
    AdminCardWithTitleComponent,
    AdminLoadMoreComponent,
    CarouselComponent,
    DateRangeSelectorComponent,
    FieldConfigValueInputComponent,
    FooterComponent,
    UserProfileDashboardBodyComponent,
    PageHeaderComponent,
    ButtonComponent,
    ButtonWithIconComponent,
    PanelComponent,
    BadgeComponent,
    TableHeaderSeparatorComponent,
    BadgeWithButtonComponent,
    MultiSelectDropdownComponent,
    TagsInputComponent,
    LandingComponent,
    ContactComponent,
    PrivacyPolicyComponent,
    RaceGridItemComponent,
    RacePricingTableComponent,
    TooltipComponent,
    TooltipDirective,
    RaceProfileComponent,
    RacesHeaderComponent,
    EventGridItemComponent,
    EventGridItemWithImage,
    LeagueGridItemWithImage,
    EventGridItemWithImageSmall,
    MonthsPickerComponent,
    DownloadAppHeroComponent,
    SignupComponent,
    LoginComponent,
    SearchBarComponent,
    SearchBarRacesComponent,
    SearchBarEventsComponent,
    FeaturedRaceCarouselComponent,
    SpinnerComponent,
    SignupVerificationComponent,
    RecoverLostPasswordComponent,
    ChangePasswordOnRecoveryComponent,
    LanguageSwitcherComponent,
    FormCardComponent,
    SectionDividerComponent,
    RaceDetailsComponent,
    RaceMapTabComponent,
    EventDetailsComponent,
    RegisterToRaceComponent,
    EventManagementRaceListComponent,
    EventManagementParticipantsListComponent,
    EventManagementRegistrationsListComponent,
    RegisterToRaceItemComponent,
    RegisterToRaceFeeItemOutputComponent,
    RegisterToRaceAdditionalItemOutput,
    ToastComponent,
    PageTemplateComponent,
    RibbonComponent,
    CompetitorTableComponent,
    ResultsTableComponent,
    ErrorStateComponent,
    PaymentComponent,
    LiveResultsComponent,
    EmptyStateComponent,
    EmptyStateAdminComponent,
    ThreeDotsComponent,
    DotIndicatorComponent,
    LiveResultsTableHeaderComponent,
    LiveEventComponent,
    LiveRaceListItemComponent,
    LiveResultsRaceHeadingComponent,
    LiveResultsRaceDetailsComponent,
    EventCalendarComponent,
    LeagueCalendarComponent,
    LandingComponent,
    UserProfileDashboardComponent,
    UserProfileEditComponent,
    UserProfileViewComponent,
    UserProfileDashboardTabComponent,
    UserProfileEventsTabComponent,
    UserProfileWishListTabComponent,
    UserProfileClubsAndTeamsTabComponent,
    AppStoreInfoModalContent,
    SearchComponent,
    SearchRoundedComponent,
    AlertWarningComponent,
    AlertErrorComponent,
    AlertSuccessComponent,
    LiveResultsMobileItemComponent,
    LiveResultsTableBodyComponent,
    BreadcrumbsComponent,
    ListOfRequestsComponent,
    RequestDetailsComponent,
    UserDetailsComponent,
    UserDetailsDataComponent,
    ListOfAdministratorsComponent,
    ListOfUsersComponent,
    OrganiserDetailsComponent,
    EventBasicDataComponent,
    DateTimeFromToInputComponent,
    OrganiserBasicDataComponent,
    AdminTemplateComponent,
    ListOfEventsComponent,
    ListOfOrganisersComponent,
    OrganiserUserListComponent,
    AdminEventDetailsComponent,
    EventInfoComponent,
    AdminRaceDetailsComponent,
    OrganizerDashboardComponent,
    OrganiserPaymentDataFormComponent,
    ConfirmationModalComponent,
    RegistrationDetailsModalComponent,
    RaceProfileModalComponent,
    ValidationModalComponent,
    ValidatorResultMessage,
    OrganiserContactModalComponent,
    AddUserModalComponent,
    NewOrganiserModalComponent,
    LanguageInputSwitcherComponent,
    EventPaymentSettingsComponent,
    RolePipe,
    ErrorMessagesPipe,
    RaceLengthFormatPipe,
    RaceCompetitorCountFormatPipe,
    RaceCommercialItemsMinPriceFormatPipe,
    RaceAscentDescentFormatPipe,
    RaceAltitudeFormatPipe,
    OrdinalNumberPipe,
    CompetitorFieldValuePipe,
    CurrencyPipe,
    ImportStatusPipe,
    FilterByFieldPipe,
    WaypointLocationPipe,
    NumberFormatterDirective,
    SortableColumn,
    CustomControlValueAccessorDirective,
    ColorMonthPipe,
    TimeLimitPipe,
    AdminMenuComponent,
    CheckmarkIndicatorComponent,
    RaceWaypointsComponent,
    RaceDetailsBasicInfoComponent,
    DragAndDropComponent,
    RaceCompetitorsListComponentComponent,
    RaceResultsListComponentComponent,
    RaceManagementComponentComponent,
    RaceDetailsAdditionalInfoComponent,
    RaceDetailsSettingsComponent,
    RaceDetailsPricingComponent,
    RaceAdditionalPricingItemFormComponent,
    RaceCommercialItemFormComponent,
    RaceCommercialItemCardComponent,
    RaceAdditionalPricingItemCardComponent,
    LanguageInputComponent,
    RaceDetailsRegistrationSettingsComponent,
    RaceDetailsRegistrationSettingsCustomFieldFormComponent,
    RaceDetailsRegistrationSettingsDefaultFieldFormComponent,
    LanguageOutputComponent,
    GridItemTitleComponent,
    EventManagementComponent,
    ListOfDiscountsComponent,
    MessagesComponent,
    DiscountsDetailsComponent,
    CategoriesListComponent,
    CategoryTypePipe,
    RegistrationTypePipe,
    RegistrationNamePipe,
    LocalizedDatePipe,
    LiveResultsFinishedCountPipe,
    LeagueEventsToGoPipe,
    LeagueNextRoundPipe,
    LeagueRoundPipe,
    FormatTimePipe,
    EventPreviewComponent,
    DateTimeInputComponent,
    DurationInputComponent,
    DropdownComponent,
    SortableTableComponent,
    DropdownToggleDirective,
    DropdownContentDirective,
    DropdownItemDirective,
    CategoryFormComponent,
    CategoryItemFormComponent,
    UserListModalComponent,
    AddDiscountItemModalComponent,
    NavTabsComponent,
    NavTabComponent,
    EventsListModalComponent,
    ImageFallbackDirective,
    RaceDetailsPanelComponent,
    StepperComponent,
    InputComponent,
    CheckpointInputComponent,
    SelectComponent,
    TelInputComponent,
    ControlPointsTableComponent,
    TextareaComponent,
    CheckboxComponent,
    SelectComponent,
    LogoXxlComponent,
    SectionSubtitleComponent,
    EntryListItemComponent,
    EventInfoTabComponent,
    LeagueInfoTabComponent,
    LeagueResultsTabComponent,
    LeagueDetailsComponent,
    YoutubeEmbedComponent,
    LoaderComponent,
    PageHeaderPublicComponent,
    InfoSectionPublicComponent,
    GreyCardWithTitleComponent,
    FullCropperWidthComponent,
    HorizontalBoxListComponent,
    HorizontalBoxListItemComponent,
    HeaderAltComponent,
    HeaderBadgeComponent,
    EventListItemComponent,
    ResultListItemComponent,
    MailboxListItemComponent,
    TeamListItemComponent,
    ShareFavouriteCtaComponent,
    ActionButtonsRowComponent,
    AdminActionsHeaderComponent,
    ListOfAccountStatementsComponent,
    AccountStatementDetailsComponent,
    ListOfLeaguesComponent,
    AdminLeagueDetailsComponent,
    LeagueBasicDataComponent,
    EventListItemHeaderComponent,
    EventListItemBigComponent,
    LeagueEventListTabComponent,
    TableSectionHeaderComponent,
    SeriesTableComponent,
    ContactsTabComponent,
    LeafletMapsWrapperComponent,
    RichTextEditorWrapperComponent
  ],
  imports: [
    NgbModule,
    NgbNavModule,
    TranslocoModule,
    FormsModule,
    SocialLoginModule,
    MatListModule,
    MatBottomSheetModule,
    MatIconModule,
    MatButtonModule,
    InfiniteScrollModule,
    NgxChartsModule,
    ReactiveFormsModule,
    MatStepperModule,
    MatDialogModule,
    NgxMaskModule.forRoot(),
    NgOptimizedImage,
    MatTooltipModule,
    SwiperModule,
    LyImageCropperModule,
    LySliderModule,
    LyIconModule,
    LyButtonModule,
    ShareButtonsModule,
    ShareIconsModule,
    GoogleSigninButtonModule,
    RouterModule,
    ReactiveFormsModule,
    SectionCardComponent,
    PageHeadingComponent,
    RoundedButtonComponent
  ],
  exports: [SpinnerComponent, ToastComponent],
  providers: [
    AuthenticationService,
    ErrorService,
    EventPaymentSettingsService,
    CodebookService,
    AdministratorsService,
    OrganiserService,
    OrganiserPaymentDataService,
    LocalStorageService,
    LoaderService,
    RaceService,
    RequestService,
    CompetitorService,
    CompetitorResultsService,
    ActionService,
    EventService,
    LiveResultsService,
    PermissionService,
    EventRaceService,
    UserDetailService,
    OrganiserUsersService,
    FormParametrizationService,
    {provide: NgbDatepickerI18n, useClass: CustomNgbDatepickerI18n},
    {provide: NgbDateAdapter, useClass: CustomNgbDatepickerAdapter},
    {provide: NgbDateParserFormatter, useClass: CustomNgbDatepickerDateParserFormatter},
    ToastService,
    WsPayService,
    WaypointService,
    RaceCommercialService,
    ApplicationFormSettingsService,
    RaceRouteService,
    CurrencyPipe,
    ColorMonthPipe,
    RaceLengthFormatPipe,
    LocalizedDatePipe,
    LeagueEventsToGoPipe,
    LeagueNextRoundPipe,
    LeagueRoundPipe,
    FormatTimePipe,
    MessageService,
    CategoriesService,
    ImageService,
    DiscountsService,
    AccountStatementsService,
    LeagueService,
    ExportService,
    DecimalPipe,
    RolePipe,
    RegistrationTypePipe,
    TimeLimitPipe,
    CategoryTypePipe,
    RegistrationNamePipe,
    {provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy},
    LyTheme2,
    LyRippleService,
    LyIconService,
    {provide: LY_THEME_NAME, useValue: 'minima-light'},
    {provide: LY_THEME, useClass: MinimaLight, multi: true}, // name: `minima-light`
    {provide: LY_THEME, useClass: MinimaDark, multi: true},
  ]
})
export class AppModule {
}
